/**
 * Browser detection utilities for onboarding
 * Detects timezone, country, and language from browser settings
 */

/**
 * Detect browser timezone using Intl API
 * @returns {string} IANA timezone (e.g., "Europe/Stockholm")
 */
// Default timezone for Sweden-only release
const DEFAULT_TIMEZONE = 'Europe/Stockholm';

export function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
  } catch (e) {
    console.warn('Failed to detect timezone:', e);
    return DEFAULT_TIMEZONE;
  }
}

/**
 * Detect country from browser locale
 * Parses navigator.language (e.g., "sv-SE" -> "SE", "en-US" -> "US")
 * Falls back to language-based inference for Nordic countries
 * @returns {string|null} ISO 3166-1 alpha-2 country code or null
 */
export function detectCountry() {
  try {
    // Try Intl.Locale API first (modern browsers)
    if (typeof Intl !== 'undefined' && Intl.Locale) {
      const locale = new Intl.Locale(navigator.language);
      if (locale.region) {
        return locale.region.toUpperCase();
      }
    }

    // Fallback: Parse navigator.language manually
    const lang = navigator.language || navigator.userLanguage;
    if (lang) {
      // Format: "en-US", "sv-SE", "en-GB"
      const parts = lang.split('-');
      if (parts.length >= 2) {
        const region = parts[parts.length - 1];
        // Validate it looks like a country code
        if (/^[A-Za-z]{2}$/.test(region)) {
          return region.toUpperCase();
        }
      }

      // Nordic language fallback - if only language code is available
      // (e.g., "sv" without region), infer likely country
      const primaryLang = parts[0].toLowerCase();
      const nordicLanguageMap = {
        'sv': 'SE',  // Swedish -> Sweden
        'da': 'DK',  // Danish -> Denmark
        'nb': 'NO',  // Norwegian Bokmål -> Norway
        'nn': 'NO',  // Norwegian Nynorsk -> Norway
        'no': 'NO',  // Norwegian -> Norway
        'fi': 'FI',  // Finnish -> Finland
        'is': 'IS',  // Icelandic -> Iceland
      };
      if (nordicLanguageMap[primaryLang]) {
        return nordicLanguageMap[primaryLang];
      }
    }

    return null;
  } catch (e) {
    console.warn('Failed to detect country:', e);
    return null;
  }
}

// Default language for Sweden-only release
const DEFAULT_LANGUAGE = 'sv';

/**
 * Detect preferred language from browser
 * @returns {string} 'en' or 'sv' (supported languages)
 */
export function detectLanguage() {
  try {
    const lang = navigator.language || navigator.userLanguage || DEFAULT_LANGUAGE;
    const primary = lang.split('-')[0].toLowerCase();
    // For Sweden-only release, default to Swedish unless explicitly English
    return primary === 'en' ? 'en' : 'sv';
  } catch (e) {
    console.warn('Failed to detect language:', e);
    return DEFAULT_LANGUAGE;
  }
}

/**
 * Detect all browser preferences at once
 * @returns {Object} { timezone, country, language }
 */
export function detectBrowserPreferences() {
  return {
    timezone: detectTimezone(),
    country: detectCountry(),
    language: detectLanguage()
  };
}
